/**
 * La subida de un archivo de la base de ventas, de a partes, como la espera el backend (`util/subida.util.ts` de
 * allá): las partes van EN ORDEN, de a una y cada una en su pedido, así ningún pedido choca con el tope de tiempo del
 * servidor ni del hosting. Si una parte no llega (se cortó la red, un 5xx, un reintento que se cruzó), se vuelve a
 * leer la carga y se sigue desde lo que el servidor dice que tiene (`partes` y `recibidos`): nunca se adivina.
 *
 * Sin React ni red: quien llama pasa cómo subir una parte y cómo leer la carga (testeado en `subida.test.ts`).
 */

import type { Lectura } from "../../wa-oficial/logic/respuesta";
import { textoError } from "../../wa-oficial/logic/respuesta";
import type { Archivo, ArchivoEnCarga, Carga } from "../types";

/** El tamaño de cada parte (el servidor acepta hasta el doble). */
export const BYTES_PARTE = 8 * 1024 * 1024;
/** Cuántas veces seguidas se reintenta una parte que no llegó antes de rendirse. */
export const MAX_REINTENTOS = 3;

/** La subida no pudo seguir: el texto es para la pantalla. `CORTADA` = la cortó quien subía (cambió de archivo). */
export class ErrorSubida extends Error {
  constructor(
    mensaje: string,
    readonly code = "",
  ) {
    super(mensaje);
    this.name = "ErrorSubida";
  }
}

export interface Subidor {
  subirParte: (p: { parte: number; bytes: number; nombre: string }, cuerpo: Blob) => Promise<Lectura<Carga>>;
  ver: () => Promise<Lectura<Carga>>;
  /** después de cada parte que llegó, con lo que tiene el servidor */
  alAvanzar?: (s: ArchivoEnCarga, carga: Carga) => void;
  /** true si quien subía la cortó: no se manda nada más */
  cortada?: () => boolean;
  esperar?: (ms: number) => Promise<void>;
}

type ArchivoLocal = Pick<File, "name" | "size" | "slice">;

const esperarDeVerdad = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/** Lo que tiene el servidor es ESTE archivo (mismo nombre y tamaño). */
const esElMismo = (f: ArchivoLocal, s: ArchivoEnCarga | undefined): s is ArchivoEnCarga => !!s && s.nombre === f.name.trim() && s.bytes === f.size;

/** Desde dónde seguir: si el servidor tiene a medias este mismo archivo, desde ahí; si no, desde el principio. */
export function seSigueDesde(f: ArchivoLocal, s: ArchivoEnCarga | undefined): { parte: number; desde: number } {
  if (esElMismo(f, s) && !s.completo && s.recibidos > 0 && s.recibidos < f.size) return { parte: s.partes, desde: s.recibidos };
  return { parte: 0, desde: 0 };
}

/** Lo que vale la pena reintentar: la red, el servidor caído o apurado, y una parte que se cruzó con un reintento. */
const reintentable = (l: { status: number; code: string }) => l.status === 0 || l.status >= 500 || l.status === 429 || l.code === "PARTE_FUERA_DE_ORDEN";

/** Sube el archivo entero (o lo que le falta). Devuelve la carga como quedó; lanza `ErrorSubida`. */
export async function subirArchivo(tipo: Archivo, f: ArchivoLocal, previo: ArchivoEnCarga | undefined, s: Subidor): Promise<Carga> {
  if (!f.size) throw new ErrorSubida("El archivo está vacío.");
  const esperar = s.esperar ?? esperarDeVerdad;
  let { parte, desde } = seSigueDesde(f, previo);
  let fallos = 0;
  const cortar = () => {
    if (s.cortada?.()) throw new ErrorSubida("", "CORTADA");
  };

  for (;;) {
    cortar();
    const r = await s.subirParte({ parte, bytes: f.size, nombre: f.name }, f.slice(desde, Math.min(f.size, desde + BYTES_PARTE)));
    cortar();
    if (r.ok) {
      const a = r.data.archivos[tipo];
      if (a && esElMismo(f, a) && (a.completo || a.recibidos > desde)) {
        fallos = 0;
        s.alAvanzar?.(a, r.data);
        if (a.completo) return r.data;
        parte = a.partes;
        desde = a.recibidos;
        continue;
      }
    } else if (!reintentable(r)) {
      throw new ErrorSubida(textoError(r), r.code);
    }

    // No llegó (o llegó algo que no avanza): se pregunta qué tiene el servidor y se sigue desde ahí.
    fallos += 1;
    if (fallos > MAX_REINTENTOS) throw new ErrorSubida(r.ok ? "El archivo no terminó de subir. Probá de nuevo." : textoError(r), r.ok ? "" : r.code);
    await esperar(1000 * fallos);
    cortar();
    const v = await s.ver();
    if (!v.ok) {
      if (reintentable(v)) continue;
      throw new ErrorSubida(textoError(v), v.code);
    }
    const a = v.data.archivos[tipo];
    // la última parte llegó y se perdió la respuesta
    if (esElMismo(f, a) && a.completo) {
      s.alAvanzar?.(a, v.data);
      return v.data;
    }
    ({ parte, desde } = seSigueDesde(f, a));
  }
}

/**
 * La carga después de la respuesta de una parte: de ese archivo vale lo nuevo y del otro, lo que ya se tenía. Las
 * dos subidas corren a la vez y cada respuesta trae el otro archivo como estaba cuando empezó ese pedido.
 */
export function fundirParte(antes: Carga | null, despues: Carga, tipo: Archivo): Carga {
  if (!antes || antes.id !== despues.id) return despues;
  const otro: Archivo = tipo === "ventas" ? "clientes" : "ventas";
  const archivos = { ...despues.archivos };
  if (antes.archivos[otro]) archivos[otro] = antes.archivos[otro];
  else delete archivos[otro];
  return { ...despues, archivos };
}

/** «Subiendo… 45 %» (de lo que llegó al servidor). */
export function avance(s: Pick<ArchivoEnCarga, "recibidos" | "bytes"> | null | undefined): number {
  if (!s || !s.bytes) return 0;
  return Math.min(100, Math.floor((s.recibidos / s.bytes) * 100));
}
